import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Component, ChangeDetectionStrategy, Output, Input, HostBinding, ContentChildren, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import * as i1 from '@firestitch/label';
import { FsLabelModule } from '@firestitch/label';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isEqual, remove } from 'lodash-es';

class FsCheckboxGroupComponent {
    constructor(iterableDiffers, _cdRef) {
        this.iterableDiffers = iterableDiffers;
        this._cdRef = _cdRef;
        this.change = new EventEmitter();
        this.orientation = 'vertical';
        this.position = 'top';
        this.disabled = null;
        this.compareWith = (o1, o2) => {
            return isEqual(o1, o2);
        };
        this.formWrapper = true;
        this.innerValue = [];
        this._destroy$ = new Subject();
        this.onTouchedCallback = () => { };
        this.onChangeCallback = () => { };
        this._differChildren = this.iterableDiffers.find([]).create(null);
    }
    ;
    ngOnChanges(changes) {
        if (this.disabled !== null && changes.disabled && this.contentChildren) {
            this.contentChildren.forEach((checkbox) => {
                checkbox.disabled = this.disabled;
            });
        }
    }
    ngAfterContentInit() {
        let changeDiff = this._differChildren.diff(this.contentChildren);
        if (changeDiff) {
            changeDiff.forEachAddedItem(change => {
                this._addCheckbox(change.item);
            });
        }
        this.contentChildren.changes
            .pipe(takeUntil(this._destroy$))
            .subscribe(fsModelObjects => {
            changeDiff = this._differChildren.diff(fsModelObjects);
            if (changeDiff) {
                changeDiff.forEachAddedItem((change) => {
                    setTimeout(() => {
                        this._addCheckbox(change.item);
                    });
                });
            }
        });
    }
    _toggleInput(input) {
        if (this.innerValue) {
            input.checked = this._valueExists(input.value);
        }
    }
    _valueExists(inputValue) {
        return this.innerValue.find((value) => {
            return this.compareWith(value, inputValue);
        }) !== undefined;
    }
    _addCheckbox(input) {
        if (this.disabled !== null) {
            input.disabled = this.disabled;
        }
        this._toggleInput(input);
        this._cdRef.markForCheck();
        input.change
            .pipe(takeUntil(this._destroy$))
            .subscribe((value) => {
            if (value.checked) {
                if (!this._valueExists(input.value)) {
                    this.innerValue.push(value.source.value);
                }
            }
            else {
                remove(this.innerValue, (item) => {
                    return this.compareWith(item, input.value);
                });
            }
            this.onChangeCallback(this.innerValue);
            this.change.emit(this.innerValue);
        });
    }
    writeValue(value) {
        if (Array.isArray(value)) {
            this.innerValue = value;
        }
        else {
            if (value !== undefined && value !== null) {
                this.innerValue = [value];
            }
            else {
                this.innerValue = [];
            }
        }
        if (this.contentChildren) {
            this.contentChildren.toArray().forEach((input) => {
                this._toggleInput(input);
            });
        }
    }
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
FsCheckboxGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCheckboxGroupComponent, deps: [{ token: i0.IterableDiffers }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsCheckboxGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsCheckboxGroupComponent, selector: "fs-checkbox-group", inputs: { orientation: "orientation", position: "position", label: "label", disabled: "disabled", compareWith: "compareWith" }, outputs: { change: "change" }, host: { properties: { "class.fs-form-wrapper": "this.formWrapper" } }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FsCheckboxGroupComponent),
            multi: true
        }], queries: [{ propertyName: "contentChildren", predicate: MatCheckbox }], usesOnChanges: true, ngImport: i0, template: "<fs-label-field>\n  <fs-label *ngIf=\"label\" class=\"fs-form-label\">{{label}}</fs-label>\n  <ng-content></ng-content>\n  <span class=\"checkboxes\" [ngClass]=\"{\n    'orientation-vertical': orientation==='vertical',\n    'position-top': position==='top',\n    'position-center': position==='center'\n  }\">\n    <ng-content select=\"mat-checkbox\"></ng-content>\n  </span>\n  <fs-label-message>\n    <div class=\"fs-form-message\">\n      <div class=\"fs-form-hint\">\n        <ng-content select=\"mat-hint\"></ng-content>\n      </div>\n    </div>\n  </fs-label-message>\n</fs-label-field>\n", styles: [":host{position:relative}:host ::ng-deep fs-label-field{position:relative;width:100%}:host ::ng-deep .checkboxes:not(.vertical) mat-checkbox{margin-right:15px}:host ::ng-deep .checkboxes:not(.vertical) mat-checkbox:last-child{margin-right:0}:host ::ng-deep .checkboxes.orientation-vertical{display:block}:host ::ng-deep .checkboxes.orientation-vertical .mat-checkbox{display:block;margin:7px 0}:host ::ng-deep .checkboxes.orientation-vertical .mat-checkbox:first-child{margin-top:0}:host ::ng-deep .checkboxes.orientation-vertical .mat-checkbox:last-child{margin-bottom:0}:host ::ng-deep .checkboxes.orientation-vertical.position-center .mat-checkbox-layout{align-items:center}:host ::ng-deep .checkboxes.orientation-vertical.position-center .mat-checkbox-layout .mat-checkbox-inner-container{margin-top:initial;margin-bottom:initial}:host ::ng-deep .checkboxes.orientation-vertical.position-top .mat-checkbox-layout{white-space:normal;align-items:flex-start}:host ::ng-deep .checkboxes.orientation-vertical.position-top .mat-checkbox-layout .mat-checkbox-inner-container{margin-top:unset}:host ::ng-deep .mat-checkbox-layout .mat-checkbox-label{line-height:normal}\n"], components: [{ type: i1.FsLabelFieldComponent, selector: "fs-label-field", inputs: ["bottomMargin", "topMargin", "labelMargin"] }, { type: i1.FsLabelComponent, selector: "fs-label" }, { type: i1.FsLabelMessageComponent, selector: "fs-label-message" }], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCheckboxGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-checkbox-group',
                    templateUrl: './checkbox-group.component.html',
                    styleUrls: ['./checkbox-group.component.scss'],
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FsCheckboxGroupComponent),
                            multi: true
                        }],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.IterableDiffers }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { change: [{
                type: Output
            }], orientation: [{
                type: Input
            }], position: [{
                type: Input
            }], label: [{
                type: Input
            }], disabled: [{
                type: Input
            }], compareWith: [{
                type: Input
            }], formWrapper: [{
                type: HostBinding,
                args: ['class.fs-form-wrapper']
            }], contentChildren: [{
                type: ContentChildren,
                args: [MatCheckbox]
            }] } });

class FsCheckboxGroupModule {
}
FsCheckboxGroupModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCheckboxGroupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsCheckboxGroupModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCheckboxGroupModule, declarations: [FsCheckboxGroupComponent], imports: [CommonModule,
        MatInputModule,
        FsLabelModule], exports: [FsCheckboxGroupComponent] });
FsCheckboxGroupModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCheckboxGroupModule, imports: [[
            CommonModule,
            MatInputModule,
            FsLabelModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCheckboxGroupModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatInputModule,
                        FsLabelModule
                    ],
                    declarations: [
                        FsCheckboxGroupComponent
                    ],
                    exports: [
                        FsCheckboxGroupComponent
                    ]
                }]
        }] });

/*
 * Public API Surface of fs-menu
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FsCheckboxGroupComponent, FsCheckboxGroupModule };
